import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { LangProvider } from "@/lib/i18n";
import { AuthProvider } from "@/contexts/AuthContext";
import { AdminAuthProvider } from "@/contexts/AdminAuthContext";

import Landing from "@/pages/Landing";
import NotFound from "@/pages/NotFound";
import FreeScanReport from "@/pages/FreeScanReport";
import AuthPage from "@/pages/AuthPage";
import AppShell from "@/components/AppShell";
import Dashboard from "@/pages/Dashboard";
import ProofsPage from "@/pages/ProofsPage";
import AlertsPage from "@/pages/AlertsPage";
import JournalPage from "@/pages/JournalPage";
import BillingPage from "@/pages/BillingPage";
import SitesPage from "@/pages/SitesPage";
import FullScanPage from "@/pages/FullScanPage";
import ImageScanPage from "@/pages/ImageScanPage";
import DocumentScanPage from "@/pages/DocumentScanPage";
import CGVPage from "@/pages/CGVPage";
import AboutPage from "@/pages/AboutPage";
import ContactPage from "@/pages/ContactPage";
import LimitPage from "@/pages/LimitPage";
import PaymentSuccess, { PaymentCancel } from "@/pages/PaymentPages";
import BadgePage from "@/pages/BadgePage";
import TrustPage from "@/pages/TrustPage";
import ComparePage from "@/pages/ComparePage";
import SubscriptionsPage from "@/pages/SubscriptionsPage";
import GuidesIndex, { GuideArticle } from "@/pages/GuidesPages";
import { LabelsDirectoryIndex, LabelsCountryPage } from "@/pages/LabelsDirectory";
import { DIRECTORY_BASE } from "@/constants/labelCountries";
import { MentionsLegales, FaqPage, LegalPage, DirectiveFullPage } from "@/pages/LegalPages";
import { ForgotPage, ResetPage } from "@/pages/PasswordPages";
import ReportsPage from "@/pages/ReportsPage";
import HowItWorksPage from "@/pages/HowItWorksPage";
import SeoPage from "@/pages/SeoPage";
import { SEO_ROUTES, LANGS, pagePath } from "@/content/seo";

// Pages non génériques servies sur une URL localisée
const SEO_COMPONENTS = {
  home: (lang) => <Landing lang={lang} />,
  guides: (lang) => <GuidesIndex lang={lang} />,
  faq: (lang) => <FaqPage lang={lang} />,
  law: (lang) => <LegalPage lang={lang} />,
  "law-text": (lang) => <DirectiveFullPage lang={lang} />,
};

import AdminAppShell from "@/layouts/AdminAppShell";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminForgot from "@/pages/admin/AdminForgot";
import AdminReset from "@/pages/admin/AdminReset";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminAuditPage from "@/pages/admin/AdminAuditPage";
import AdminCorpus from "@/pages/admin/AdminCorpus";
import AdminDocs from "@/pages/admin/AdminDocs";
import AdminClients from "@/pages/admin/AdminClients";
import AdminClientDetail from "@/pages/admin/AdminClientDetail";
import AdminScans from "@/pages/admin/AdminScans";
import AdminBilling from "@/pages/admin/AdminBilling";
import AdminCompliance from "@/pages/admin/AdminCompliance";
import AdminNda from "@/pages/admin/AdminNda";
import NdaPage from "@/pages/NdaPage";
import AdminExploitation from "@/pages/admin/AdminExploitation";
import MaintenanceBanner from "@/components/MaintenanceBanner";
import CookieConsent from "@/components/CookieConsent";

export default function App() {
  return (
    <div className="App">
      <LangProvider>
        <AuthProvider>
          <AdminAuthProvider>
            <BrowserRouter>
              <MaintenanceBanner />
              <Routes>
                <Route path="/" element={<Landing />} />

                {/* Pages SEO/GEO localisées — une URL, un title, un canonical par langue */}
                {SEO_ROUTES.filter((r) => r.path !== "/").map((r) => (
                  <Route
                    key={r.path}
                    path={r.path}
                    element={SEO_COMPONENTS[r.key] ? SEO_COMPONENTS[r.key](r.lang) : <SeoPage pageKey={r.key} lang={r.lang} />}
                  />
                ))}

                <Route path="/scan/:id" element={<FreeScanReport />} />
                <Route path="/limite" element={<LimitPage />} />
                <Route path="/cgv" element={<CGVPage />} />
                <Route path="/a-propos" element={<AboutPage />} />
                <Route path="/:lang(en|de|es|it|nl)/a-propos" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/register" element={<AuthPage mode="register" />} />
                <Route path="/login" element={<AuthPage mode="login" />} />
                <Route path="/forgot-password" element={<ForgotPage />} />
                <Route path="/reset-password" element={<ResetPage />} />
                <Route path="/payment/success" element={<PaymentSuccess />} />
                <Route path="/payment/cancel" element={<PaymentCancel />} />
                <Route path="/badge/:id" element={<BadgePage />} />
                <Route path="/trust/:id" element={<TrustPage />} />
                <Route path="/guides" element={<GuidesIndex />} />
                {/* Annuaire public des labels environnementaux UE-27 — 6 langues × (index + 27 pays) */}
                {Object.entries(DIRECTORY_BASE).flatMap(([l, base]) => [
                  <Route key={`dir-${l}`} path={base} element={<LabelsDirectoryIndex lang={l} />} />,
                  <Route key={`dir-${l}-c`} path={`${base}/:slug`} element={<LabelsCountryPage lang={l} />} />,
                ])}
                <Route path="/guides/:slug" element={<GuideArticle />} />
                {LANGS.filter((l) => l !== "fr").map((l) => (
                  <Route key={l} path={`${pagePath("guides", l)}/:slug`} element={<GuideArticle lang={l} />} />
                ))}
                <Route path="/mentions-legales" element={<MentionsLegales />} />
                <Route path="/faq" element={<FaqPage />} />
                <Route path="/legal" element={<LegalPage />} />
                <Route path="/legal/directive-2024-825" element={<DirectiveFullPage />} />

                {/* Admin — auth isolée */}
                {/* Page cachée de signature du NDA (URL secrète, noindex) */}
                <Route path="/nda/:token" element={<NdaPage />} />
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin/forgot" element={<AdminForgot />} />
                <Route path="/admin/reset" element={<AdminReset />} />
                <Route path="/admin" element={<AdminAppShell />}>
                  <Route index element={<AdminDashboard />} />
                  <Route path="clients" element={<AdminClients />} />
                  <Route path="clients/:id" element={<AdminClientDetail />} />
                  <Route path="scans" element={<AdminScans />} />
                  <Route path="billing" element={<AdminBilling />} />
                  <Route path="compliance" element={<AdminCompliance />} />
                  <Route path="nda" element={<AdminNda />} />
                  <Route path="exploitation" element={<AdminExploitation />} />
                  <Route path="audit" element={<AdminAuditPage />} />
                  <Route path="corpus" element={<AdminCorpus />} />
                  <Route path="docs" element={<AdminDocs />} />
                </Route>

                <Route path="/app" element={<AppShell />}>
                  <Route index element={<Dashboard />} />
                  <Route path="sites" element={<SitesPage />} />
                  <Route path="proofs" element={<ProofsPage />} />
                  <Route path="alerts" element={<AlertsPage />} />
                  <Route path="journal" element={<JournalPage />} />
                  <Route path="billing" element={<BillingPage />} />
                  <Route path="reports" element={<ReportsPage />} />
                  <Route path="compare" element={<ComparePage />} />
                  <Route path="subscriptions" element={<SubscriptionsPage />} />
                  <Route path="scan" element={<FullScanPage />} />
                  <Route path="scan/:id" element={<FullScanPage />} />
                  <Route path="image-scan" element={<ImageScanPage />} />
                  <Route path="document-scan" element={<DocumentScanPage />} />
                  <Route path="how-it-works" element={<HowItWorksPage />} />
                </Route>
                {/* Fallback 404 — <meta robots noindex> côté client */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </BrowserRouter>
            <Toaster position="top-right" richColors closeButton />
            <CookieConsent />
          </AdminAuthProvider>
        </AuthProvider>
      </LangProvider>
    </div>
  );
}
